import { useState, useMemo, useEffect } from 'react';
import type { QuoteRequest } from '@/types/quote';
import { ExtractedDataPanel } from './extracted-data-panel';
import { Badge, BadgeWithIcon } from '@/components/base/badges/badges';
import { useTranslation } from 'react-i18next';
import { Mail01, CheckCircle, AlertCircle, X, Send01, Save01 } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { Input } from '@/components/base/input/input';
import { TextArea } from '@/components/base/textarea/textarea';
import { quoteService, shipperQuoteService } from '@/data/mock-quotes';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';
import { useAccount } from '@/providers/account-provider';
import { accountService } from '@/data/mock-accounts';
import { Modal, ModalOverlay, Dialog } from '@/components/application/modals/modal';

// Simple markdown parser for email content
const parseMarkdown = (text: string): string => {
    if (!text) return '';

    let html = text;

    // Convert **bold** to <strong> (process first to avoid conflicts)
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Convert *italic* to <em> (only single asterisks, not part of **)
    // Match single * that are not preceded or followed by another *
    html = html.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');

    // Convert line breaks to <br>
    html = html.replace(/\n/g, '<br>');

    return html;
};

interface QuoteDetailPanelProps {
    quote: QuoteRequest;
    onClose: () => void;
    initialTab?: 'thread' | 'extracted';
}

export const QuoteDetailPanel = ({ quote: initialQuote, onClose, initialTab }: QuoteDetailPanelProps) => {
    const { t } = useTranslation('pages:quotes');
    const { selectedAccountId } = useAccount();
    const [quote, setQuote] = useState<QuoteRequest>(initialQuote);
    const [activeTab, setActiveTab] = useState<'thread' | 'extracted'>(() => {
        return initialTab === 'extracted' ? 'extracted' : 'thread';
    });
    const [responseSubject, setResponseSubject] = useState('');
    const [responseBody, setResponseBody] = useState('');
    const [responsePrice, setResponsePrice] = useState<string>('');
    const [responseValidity, setResponseValidity] = useState<string>('30');
    const [responseTerms, setResponseTerms] = useState<string>('Preventivo valido per 30 giorni. Pagamento anticipato richiesto.');
    const [isSendingResponse, setIsSendingResponse] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);

    // Debug: log quando showSuccessModal cambia
    useEffect(() => {
        console.log('[QuoteDetailPanel] showSuccessModal changed to:', showSuccessModal);
    }, [showSuccessModal]);
    const [bookingRequested, setBookingRequested] = useState<boolean>(!!initialQuote.hasBookingEmail);

    // Aggiorna la quote quando cambia la prop iniziale
    useEffect(() => {
        setQuote(initialQuote);
    }, [initialQuote]);
    const [isRequestingBooking, setIsRequestingBooking] = useState(false);
    const [bookingNumber, setBookingNumber] = useState<string>('');
    const [isBookingNumberReceived, setIsBookingNumberReceived] = useState(false);
    const [isSharingBookingNumber, setIsSharingBookingNumber] = useState(false);
    const [showBookingSection, setShowBookingSection] = useState(false);
    const [showBookingShareSuccessModal, setShowBookingShareSuccessModal] = useState(false);

    type ThreadMessageKind =
        | 'request_email'
        | 'ff_auto_response'
        | 'ff_manual_response'
        | 'shipper_modification_request'
        | 'ff_counter_proposal'
        | 'booking_email';

    interface ThreadMessage {
        id: string;
        kind: ThreadMessageKind;
        fromLabel: string;
        role: 'customer' | 'freight_forwarder' | 'shipper' | 'carrier';
        timestamp?: Date;
        subject?: string;
        body: string;
        statusBadge?: {
            label: string;
            color: 'success' | 'warning' | 'error' | 'gray';
        };
    }

    const shipperStatusBadge = useMemo(() => {
        if (!quote.shipperStatus) return undefined;
        switch (quote.shipperStatus) {
            case 'accepted':
                return { label: t('shipperStatus.accepted'), color: 'success' as const };
            case 'rejected':
                return { label: t('shipperStatus.rejected'), color: 'error' as const };
            case 'modification_requested':
                return { label: t('shipperStatus.modificationRequested'), color: 'warning' as const };
            default:
                return undefined;
        }
    }, [quote.shipperStatus, t]);

    const threadMessages: ThreadMessage[] = useMemo(() => {
        const messages: ThreadMessage[] = [];

        // Email richiesta originale (cliente -> FF)
        messages.push({
            id: 'request',
            kind: 'request_email',
            fromLabel: quote.fromName ? `${quote.fromName} <${quote.from}>` : quote.from,
            role: 'customer',
            timestamp: quote.receivedAt,
            subject: quote.subject,
            body: quote.body,
        });

        // Risposta auto FF o manuale (se esiste)
        if (quote.responseSubject && quote.responseBody) {
            messages.push({
                id: 'ff-response',
                kind: quote.autoResponded ? 'ff_auto_response' : 'ff_manual_response',
                fromLabel: t('detail.thread.freightForwarderLabel', { defaultValue: 'Freight Forwarder' }),
                role: 'freight_forwarder',
                timestamp: quote.respondedAt,
                subject: quote.responseSubject,
                body: quote.responseBody,
            });
        }

        // Eventuale richiesta modifica shipper
        if (quote.shipperStatus === 'modification_requested') {
            messages.push({
                id: 'shipper-modification',
                kind: 'shipper_modification_request',
                fromLabel: t('detail.thread.shipperLabel', { defaultValue: 'Shipper' }),
                role: 'shipper',
                timestamp: quote.lastUpdated, // Usa lastUpdated come timestamp della modifica
                body: quote.shipperMessagePreview || t('detail.thread.modificationRequestDefault', { defaultValue: 'Richiesta di modifica del preventivo' }),
                statusBadge: shipperStatusBadge,
            });
        }

        // Eventuale accettazione o rifiuto shipper
        if (quote.shipperStatus === 'accepted') {
            messages.push({
                id: 'shipper-accepted',
                kind: 'shipper_modification_request', // Reuse kind for display
                fromLabel: t('detail.thread.shipperLabel', { defaultValue: 'Shipper' }),
                role: 'shipper',
                body: t('detail.thread.acceptedMessage', { defaultValue: 'Il preventivo è stato accettato.' }),
                statusBadge: shipperStatusBadge,
            });
        }

        if (quote.shipperStatus === 'rejected') {
            messages.push({
                id: 'shipper-rejected',
                kind: 'shipper_modification_request', // Reuse kind for display
                fromLabel: t('detail.thread.shipperLabel', { defaultValue: 'Shipper' }),
                role: 'shipper',
                body: t('detail.thread.rejectedMessage', { defaultValue: 'Il preventivo è stato rifiutato.' }),
                statusBadge: shipperStatusBadge,
            });
        }

        // Eventuale mail booking all'armatore
        if (bookingRequested) {
            messages.push({
                id: 'booking',
                kind: 'booking_email',
                fromLabel: t('detail.thread.carrierLabel', { defaultValue: 'Armatore' }),
                role: 'carrier',
                body: t('detail.thread.bookingEmailBody', {
                    defaultValue:
                        'È stata generata una mail di richiesta booking all’armatore sulla base di questa quote.',
                }),
                statusBadge: shipperStatusBadge,
            });
        }

        return messages;
    }, [quote, shipperStatusBadge, t, bookingRequested]);

    const handleSaveDraft = () => {
        // TODO: Implement save draft functionality
        console.log('Saving draft:', { subject: responseSubject, body: responseBody });
    };

    const handleSendResponse = async () => {
        console.log('[QuoteDetailPanel] handleSendResponse called');
        if (!responseSubject.trim() || !responseBody.trim()) {
            console.log('[QuoteDetailPanel] Form validation failed');
            return;
        }

        console.log('[QuoteDetailPanel] Starting send process...');
        setIsSendingResponse(true);

        try {
            // Simula delay di invio (2-5 secondi)
            console.log('[QuoteDetailPanel] Simulating delay...');
            await new Promise(resolve => setTimeout(resolve, Math.random() * 3000 + 2000));
            console.log('[QuoteDetailPanel] Delay completed');

            // Ottieni l'account corrente del Freight Forwarder
            const currentAccount = accountService.getById(selectedAccountId);
            console.log('[QuoteDetailPanel] Current account:', currentAccount);
            if (!currentAccount || currentAccount.userType !== 'FREIGHT_FORWARDER') {
                console.error('[QuoteDetailPanel] Current account is not a Freight Forwarder');
                setIsSendingResponse(false);
                return;
            }

            // Trova lo shipper account dall'email nella quote
            console.log('[QuoteDetailPanel] Looking for shipper with email:', quote.from);
            const shipperAccount = accountService.getAll().find(acc => acc.email === quote.from);
            console.log('[QuoteDetailPanel] Found shipper account:', shipperAccount);
            if (!shipperAccount) {
                console.error('[QuoteDetailPanel] Could not find shipper account');
                setIsSendingResponse(false);
                return;
            }

            // Trova la ShipperQuoteRequest corrispondente per subject e shipperId
            console.log('[QuoteDetailPanel] Looking for matching request with subject:', quote.subject);
            const allShipperRequests = shipperQuoteService.getAll(shipperAccount.id);
            console.log('[QuoteDetailPanel] All shipper requests:', allShipperRequests);
            const matchingRequest = allShipperRequests.find(req =>
                req.subject === quote.subject
            );
            console.log('[QuoteDetailPanel] Matching request:', matchingRequest);

            if (!matchingRequest) {
                console.log('[QuoteDetailPanel] No matching request found, searching in all requests...');
                // Se non troviamo una corrispondenza, cerca in tutte le richieste (per compatibilità)
                const allRequests = shipperQuoteService.getAll('');
                console.log('[QuoteDetailPanel] All requests:', allRequests);
                // Cerca per subject E per from email (più flessibile)
                const requestBySubject = allRequests.find(req =>
                    req.subject === quote.subject && req.sentAt && quote.receivedAt &&
                    Math.abs(req.sentAt.getTime() - quote.receivedAt.getTime()) < 300000 // 5 minuti di tolleranza
                );
                console.log('[QuoteDetailPanel] Request by subject:', requestBySubject);

                if (!requestBySubject) {
                    console.warn('[QuoteDetailPanel] Could not find matching ShipperQuoteRequest for subject:', quote.subject);
                    // Anche se non troviamo la ShipperQuoteRequest, aggiungiamo comunque la risposta
                    // e mostriamo il modale di successo perché la risposta è stata comunque inviata
                    console.log('[QuoteDetailPanel] Proceeding anyway - response will be added to QuoteRequest only');

                    // Aggiorna lo stato della QuoteRequest del Freight Forwarder
                    const updatedQuote = quoteService.update(quote.id, {
                        status: 'responded',
                        respondedAt: new Date(),
                        responseSubject: responseSubject,
                        responseBody: responseBody,
                    });
                    console.log('[QuoteDetailPanel] quoteService.update result:', updatedQuote);

                    if (updatedQuote) {
                        setQuote(updatedQuote);
                    }

                    // Reset form
                    setResponseSubject('');
                    setResponseBody('');
                    setResponsePrice('');
                    setResponseValidity('30');
                    setResponseTerms('Preventivo valido per 30 giorni. Pagamento anticipato richiesto.');

                    // Mostra modale di successo comunque
                    console.log('[QuoteDetailPanel] Setting showSuccessModal to true (fallback)');
                    setShowSuccessModal(true);
                    setIsSendingResponse(false);
                    return;
                }

                // Crea la QuoteResponse
                console.log('[QuoteDetailPanel] Creating quote response...');
                const priceValue = responsePrice.trim() ? parseFloat(responsePrice) : (quote.extractedData?.estimatedValue ? Math.round(quote.extractedData.estimatedValue * 0.15) : 2000);
                const validityValue = responseValidity.trim() ? parseInt(responseValidity, 10) : 30;
                const quoteResponse = {
                    id: `qr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    from: currentAccount.email,
                    fromName: currentAccount.legalName,
                    subject: responseSubject,
                    body: responseBody,
                    receivedAt: new Date(),
                    quoteRequestId: requestBySubject.id,
                    price: priceValue,
                    currency: quote.extractedData?.currency || 'EUR',
                    validity: validityValue,
                    terms: responseTerms.trim() || 'Preventivo valido per 30 giorni.',
                    extractedData: quote.extractedData ? { ...quote.extractedData } : undefined,
                    isRead: false,
                    status: 'pending' as const,
                };
                console.log('[QuoteDetailPanel] Quote response created:', quoteResponse);

                // Aggiungi la risposta alla ShipperQuoteRequest
                console.log('[QuoteDetailPanel] Adding quote response to request:', requestBySubject.id);
                const success = shipperQuoteService.addQuoteResponse(requestBySubject.id, quoteResponse);
                console.log('[QuoteDetailPanel] addQuoteResponse result:', success);

                if (success) {
                    console.log('[QuoteDetailPanel] Quote response added successfully, updating quote status...');
                    // Aggiorna lo stato della QuoteRequest del Freight Forwarder
                    const updatedQuote = quoteService.update(quote.id, {
                        status: 'responded',
                        respondedAt: new Date(),
                        responseSubject: responseSubject,
                        responseBody: responseBody,
                        responseEmailId: quoteResponse.id,
                    });
                    console.log('[QuoteDetailPanel] quoteService.update result:', updatedQuote);

                    if (updatedQuote) {
                        // Aggiorna lo stato locale immediatamente
                        setQuote(updatedQuote);
                    }

                    // Reset form
                    setResponseSubject('');
                    setResponseBody('');
                    setResponsePrice('');
                    setResponseValidity('30');
                    setResponseTerms('Preventivo valido per 30 giorni. Pagamento anticipato richiesto.');

                    // Mostra modale di successo SEMPRE se la risposta è stata aggiunta
                    console.log('[QuoteDetailPanel] About to set showSuccessModal to true');
                    setShowSuccessModal(true);
                    console.log('[QuoteDetailPanel] showSuccessModal set to true');
                    console.log('[QuoteDetailPanel] Response sent successfully');
                } else {
                    console.error('[QuoteDetailPanel] Failed to add quote response');
                }
            } else {
                console.log('[QuoteDetailPanel] Found matching request, checking if this is a modification response...');

                // Controlla se questa è una risposta a una richiesta di modifica
                if (quote.shipperStatus === 'modification_requested') {
                    // Trova la quote esistente con status "modification_requested"
                    const existingQuoteWithModification = matchingRequest.receivedQuotes.find(
                        (qr) => qr.from === currentAccount.email && qr.status === 'modification_requested'
                    );

                    if (existingQuoteWithModification) {
                        console.log('[QuoteDetailPanel] Found existing quote with modification_requested, updating it...');
                        // Aggiorna la quote esistente invece di crearne una nuova
                        const priceValue = responsePrice.trim() ? parseFloat(responsePrice) : existingQuoteWithModification.price;
                        const validityValue = responseValidity.trim() ? parseInt(responseValidity, 10) : existingQuoteWithModification.validity;

                        const updated = shipperQuoteService.createUpdatedQuoteAfterModification(
                            matchingRequest.id,
                            existingQuoteWithModification.id,
                            responseBody,
                            priceValue,
                            responseSubject,
                            validityValue,
                            responseTerms.trim() || undefined
                        );

                        if (updated) {
                            // Aggiorna lo stato della QuoteRequest del Freight Forwarder
                            const updatedQuote = quoteService.update(quote.id, {
                                status: 'responded',
                                respondedAt: new Date(),
                                responseSubject: responseSubject,
                                responseBody: responseBody,
                                responseEmailId: existingQuoteWithModification.id,
                                shipperStatus: undefined, // Rimuovi lo status di modifica richiesta
                            });
                            console.log('[QuoteDetailPanel] quoteService.update result:', updatedQuote);

                            if (updatedQuote) {
                                setQuote(updatedQuote);
                            }

                            // Reset form
                            setResponseSubject('');
                            setResponseBody('');
                            setResponsePrice('');
                            setResponseValidity('30');
                            setResponseTerms('Preventivo valido per 30 giorni. Pagamento anticipato richiesto.');

                            // Mostra modale di successo
                            setShowSuccessModal(true);
                            console.log('[QuoteDetailPanel] Updated quote response sent successfully');
                        } else {
                            console.error('[QuoteDetailPanel] Failed to update quote response');
                        }
                    } else {
                        console.log('[QuoteDetailPanel] No existing quote with modification_requested found, creating new response...');
                        // Se non troviamo una quote esistente con modification_requested, crea una nuova risposta
                        const priceValue = responsePrice.trim() ? parseFloat(responsePrice) : (quote.extractedData?.estimatedValue ? Math.round(quote.extractedData.estimatedValue * 0.15) : 2000);
                        const validityValue = responseValidity.trim() ? parseInt(responseValidity, 10) : 30;
                        const quoteResponse = {
                            id: `qr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                            from: currentAccount.email,
                            fromName: currentAccount.legalName,
                            subject: responseSubject,
                            body: responseBody,
                            receivedAt: new Date(),
                            quoteRequestId: matchingRequest.id,
                            price: priceValue,
                            currency: quote.extractedData?.currency || 'EUR',
                            validity: validityValue,
                            terms: responseTerms.trim() || 'Preventivo valido per 30 giorni.',
                            extractedData: quote.extractedData ? { ...quote.extractedData } : undefined,
                            isRead: false,
                            status: 'pending' as const,
                        };

                        const success = shipperQuoteService.addQuoteResponse(matchingRequest.id, quoteResponse);
                        console.log('[QuoteDetailPanel] addQuoteResponse result:', success);

                        if (success) {
                            const updatedQuote = quoteService.update(quote.id, {
                                status: 'responded',
                                respondedAt: new Date(),
                                responseSubject: responseSubject,
                                responseBody: responseBody,
                                responseEmailId: quoteResponse.id,
                                shipperStatus: undefined,
                            });
                            console.log('[QuoteDetailPanel] quoteService.update result:', updatedQuote);

                            if (updatedQuote) {
                                setQuote(updatedQuote);
                            }

                            setResponseSubject('');
                            setResponseBody('');
                            setResponsePrice('');
                            setResponseValidity('30');
                            setResponseTerms('Preventivo valido per 30 giorni. Pagamento anticipato richiesto.');

                            setShowSuccessModal(true);
                            console.log('[QuoteDetailPanel] Response sent successfully');
                        } else {
                            console.error('[QuoteDetailPanel] Failed to add quote response');
                        }
                    }
                } else {
                    console.log('[QuoteDetailPanel] Normal response (not modification), creating new quote response...');
                    // Crea la QuoteResponse
                    const priceValue = responsePrice.trim() ? parseFloat(responsePrice) : (quote.extractedData?.estimatedValue ? Math.round(quote.extractedData.estimatedValue * 0.15) : 2000);
                    const validityValue = responseValidity.trim() ? parseInt(responseValidity, 10) : 30;
                    const quoteResponse = {
                        id: `qr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        from: currentAccount.email,
                        fromName: currentAccount.legalName,
                        subject: responseSubject,
                        body: responseBody,
                        receivedAt: new Date(),
                        quoteRequestId: matchingRequest.id,
                        price: priceValue,
                        currency: quote.extractedData?.currency || 'EUR',
                        validity: validityValue,
                        terms: responseTerms.trim() || 'Preventivo valido per 30 giorni.',
                        extractedData: quote.extractedData ? { ...quote.extractedData } : undefined,
                        isRead: false,
                        status: 'pending' as const,
                    };

                    // Aggiungi la risposta alla ShipperQuoteRequest
                    const success = shipperQuoteService.addQuoteResponse(matchingRequest.id, quoteResponse);
                    console.log('[QuoteDetailPanel] addQuoteResponse result:', success);

                    if (success) {
                        // Aggiorna lo stato della QuoteRequest del Freight Forwarder
                        const updatedQuote = quoteService.update(quote.id, {
                            status: 'responded',
                            respondedAt: new Date(),
                            responseSubject: responseSubject,
                            responseBody: responseBody,
                            responseEmailId: quoteResponse.id,
                        });
                        console.log('[QuoteDetailPanel] quoteService.update result:', updatedQuote);

                        if (updatedQuote) {
                            // Aggiorna lo stato locale immediatamente
                            setQuote(updatedQuote);
                        }

                        // Reset form
                        setResponseSubject('');
                        setResponseBody('');
                        setResponsePrice('');
                        setResponseValidity('30');
                        setResponseTerms('Preventivo valido per 30 giorni. Pagamento anticipato richiesto.');

                        // Mostra modale di successo SEMPRE se la risposta è stata aggiunta
                        console.log('[QuoteDetailPanel] Setting showSuccessModal to true');
                        setShowSuccessModal(true);
                        console.log('[QuoteDetailPanel] Response sent successfully');
                    } else {
                        console.error('[QuoteDetailPanel] Failed to add quote response');
                    }
                }
            }
        } catch (error) {
            console.error('[QuoteDetailPanel] Error sending response:', error);
        } finally {
            setIsSendingResponse(false);
        }
    };

    const getStatusColor = (status: QuoteRequest['status']) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'auto_responded': return 'success';
            case 'requires_review': return 'warning';
            case 'responded': return 'success';
            case 'archived': return 'gray';
            default: return 'gray';
        }
    };

    const translateStatus = (status: QuoteRequest['status']) => {
        const statusMap: Record<string, string> = {
            'pending': 'status.pending',
            'auto_responded': 'status.autoResponded',
            'requires_review': 'status.requiresReview',
            'responded': 'status.responded',
            'archived': 'status.archived',
        };
        return t(statusMap[status]);
    };

    return (
        <div className="flex flex-col h-full bg-primary border-l border-secondary">
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b border-secondary">
                <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Mail01 className="h-5 w-5 text-tertiary flex-shrink-0" />
                        <h3 className="text-lg font-semibold text-primary truncate">{quote.subject}</h3>
                    </div>
                    <div className="flex items-center gap-2">
                        {quote.shipperStatus === 'accepted' && !bookingRequested && !isBookingNumberReceived && (
                            <Button
                                onClick={() => {
                                    setShowBookingSection(!showBookingSection);
                                    if (!showBookingSection && !bookingRequested) {
                                        // Avvia la richiesta quando si apre la sezione
                                        setIsRequestingBooking(true);
                                        const delay = 5000 + Math.random() * 10000;
                                        setTimeout(async () => {
                                            const updated = quoteService.requestBookingNumberForQuote(quote.id);
                                            if (updated) {
                                                setBookingRequested(true);
                                                setIsRequestingBooking(false);
                                                setTimeout(() => {
                                                    setIsBookingNumberReceived(true);
                                                    const simulatedBookingNumber = `BK-${Date.now().toString().slice(-8)}`;
                                                    setBookingNumber(simulatedBookingNumber);
                                                    quoteService.setBookingNumberForQuote(quote.id, simulatedBookingNumber);
                                                }, 1000);
                                            }
                                        }, delay);
                                    }
                                }}
                                color="primary"
                                size="sm"
                                iconLeading={Send01}
                                isDisabled={isRequestingBooking}
                                isLoading={isRequestingBooking}
                            >
                                {isRequestingBooking
                                    ? (t('detail.thread.requestingBooking') || 'Richiesta in corso...')
                                    : (t('detail.thread.bookingRequestAction') || "Richiedi booking all'armatore")}
                            </Button>
                        )}
                        <Button
                            onClick={onClose}
                            color="secondary"
                            size="sm"
                            iconLeading={X}
                            aria-label="Close"
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Badge color={getStatusColor(quote.status)} size="sm">
                        {translateStatus(quote.status)}
                    </Badge>
                    {quote.autoResponded && !quote.requiresManualReview && (
                        <BadgeWithIcon color="success" size="sm" iconLeading={CheckCircle}>
                            {t('badges.autoResponded')}
                        </BadgeWithIcon>
                    )}
                    {quote.requiresManualReview && !quote.autoResponded && (
                        <BadgeWithIcon color="warning" size="sm" iconLeading={AlertCircle}>
                            {t('badges.requiresReview')}
                        </BadgeWithIcon>
                    )}
                    {shipperStatusBadge && (
                        <BadgeWithIcon color={shipperStatusBadge.color} size="sm" iconLeading={CheckCircle}>
                            {shipperStatusBadge.label}
                        </BadgeWithIcon>
                    )}
                </div>
                {quote.reviewReason && (
                    <p className="text-sm text-warning mt-2">{quote.reviewReason}</p>
                )}
            </div>

            {/* Booking Section - Mostrata tra header e tabs */}
            {quote.shipperStatus === 'accepted' && showBookingSection && (
                <div className="flex-shrink-0 px-6 py-4 border-b border-secondary bg-secondary/30">
                    {isRequestingBooking && (
                        <div className="flex items-center gap-3 p-4 bg-secondary rounded-lg">
                            <LoadingIndicator type="line-spinner" size="sm" />
                            <span className="text-sm text-tertiary">
                                {t('detail.thread.requestingBookingMessage') || 'Richiesta booking number al vettore in corso...'}
                            </span>
                        </div>
                    )}

                    {bookingRequested && isBookingNumberReceived && (
                        <div className="space-y-3">
                            <div className="text-sm font-medium text-primary">
                                {t('detail.thread.bookingNumberReceived') || 'Booking number ricevuto'}
                            </div>
                            <div className="flex items-end gap-3">
                                <Input
                                    label={t('detail.thread.bookingNumber') || 'Booking Number'}
                                    value={bookingNumber}
                                    onChange={setBookingNumber}
                                    size="md"
                                    placeholder="BK-12345678"
                                />
                                <Button
                                    onClick={() => {
                                        const simulatedBookingNumber = `BK-${Date.now().toString().slice(-8)}`;
                                        setBookingNumber(simulatedBookingNumber);
                                        quoteService.setBookingNumberForQuote(quote.id, simulatedBookingNumber);
                                    }}
                                    color="secondary"
                                    size="md"
                                >
                                    {t('detail.thread.simulateBookingNumber') || 'Simula'}
                                </Button>
                            </div>
                            <Button
                                onClick={async () => {
                                    if (!bookingNumber.trim()) return;

                                    setIsSharingBookingNumber(true);
                                    await new Promise(resolve => setTimeout(resolve, 1000));

                                    // Ottieni l'email del Freight Forwarder corrente
                                    const currentAccount = accountService.getById(selectedAccountId);
                                    const ffEmail = currentAccount?.email;

                                    quoteService.setBookingNumberForQuote(quote.id, bookingNumber, ffEmail);
                                    quoteService.shareBookingNumberWithShipper(quote.id);

                                    setIsSharingBookingNumber(false);
                                    setShowBookingShareSuccessModal(true);
                                }}
                                color="primary"
                                size="sm"
                                iconLeading={isSharingBookingNumber ? undefined : Send01}
                                isLoading={isSharingBookingNumber}
                                isDisabled={!bookingNumber.trim() || isSharingBookingNumber}
                            >
                                {isSharingBookingNumber
                                    ? (t('detail.thread.sharingBookingNumber') || 'Condivisione...')
                                    : (t('detail.thread.shareBookingNumber') || 'Condividi booking number con Shipper')}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Tabs */}
            <div className="flex-shrink-0 flex gap-2 border-b border-secondary px-6">
                <button
                    onClick={() => setActiveTab('thread')}
                    className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'thread'
                        ? 'border-b-2 border-brand-600 text-brand-600'
                        : 'text-tertiary hover:text-primary'
                        }`}
                >
                    {t('detail.tabs.thread')}
                </button>
                {quote.extractedData && (
                    <button
                        onClick={() => setActiveTab('extracted')}
                        className={`px-4 py-3 text-sm font-medium transition-colors ${activeTab === 'extracted'
                            ? 'border-b-2 border-brand-600 text-brand-600'
                            : 'text-tertiary hover:text-primary'
                            }`}
                    >
                        {t('detail.tabs.extractedData')}
                    </button>
                )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
                {/* Thread Tab */}
                {activeTab === 'thread' && (
                    <div className="space-y-6">
                        <div className="space-y-4">
                            {threadMessages.map((message) => (
                                <div
                                    key={message.id}
                                    className="rounded-lg bg-secondary p-4 shadow-xs ring-1 ring-secondary"
                                >
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                        <div className="flex items-center gap-2">
                                            <Mail01 className="h-4 w-4 text-tertiary" />
                                            <span className="text-sm font-medium text-primary">
                                                {message.fromLabel}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {message.timestamp && (
                                                <span className="text-xs text-tertiary">
                                                    {message.timestamp.toLocaleDateString('it-IT', {
                                                        day: '2-digit',
                                                        month: 'short',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                    })}
                                                </span>
                                            )}
                                            {message.statusBadge && (
                                                <Badge color={message.statusBadge.color} size="sm">
                                                    {message.statusBadge.label}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    {message.subject && (
                                        <div className="text-sm font-semibold text-primary mb-2">
                                            {message.subject}
                                        </div>
                                    )}
                                    <div className="text-sm text-primary whitespace-pre-wrap">
                                        {message.kind === 'ff_auto_response' || message.kind === 'ff_manual_response'
                                            ? (
                                                <div
                                                    className="text-sm text-primary"
                                                    dangerouslySetInnerHTML={{ __html: parseMarkdown(message.body) }}
                                                />
                                            )
                                            : message.body}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Compose / Draft section */}
                        <div className="border-t border-secondary pt-6 mt-4">
                            <h3 className="text-md font-semibold text-primary mb-4">
                                {quote.shipperStatus === 'modification_requested'
                                    ? t('detail.createResponse.titleCounterProposal')
                                    : t('detail.createResponse.title')}
                            </h3>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Form Section */}
                                <div className="space-y-4">
                                    <Input
                                        label={t('detail.createResponse.subject')}
                                        placeholder={t('detail.createResponse.subjectPlaceholder')}
                                        value={responseSubject}
                                        onChange={setResponseSubject}
                                        size="md"
                                        isRequired
                                    />
                                    <TextArea
                                        label={t('detail.createResponse.body')}
                                        placeholder={t('detail.createResponse.bodyPlaceholder')}
                                        value={responseBody}
                                        onChange={setResponseBody}
                                        rows={10}
                                        isRequired
                                        hint={t('detail.createResponse.bodyHint')}
                                    />

                                    {/* Quote Details */}
                                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-secondary">
                                        <div>
                                            <Input
                                                label={t('detail.createResponse.price') || 'Prezzo'}
                                                placeholder={t('detail.createResponse.pricePlaceholder') || '0.00'}
                                                value={responsePrice}
                                                onChange={setResponsePrice}
                                                size="md"
                                                hint={quote.extractedData?.currency ? `Valuta: ${quote.extractedData.currency}` : undefined}
                                            />
                                            <input
                                                type="number"
                                                step="0.01"
                                                className="hidden"
                                                aria-hidden="true"
                                            />
                                        </div>
                                        <div>
                                            <Input
                                                label={t('detail.createResponse.validity') || 'Validità (giorni)'}
                                                placeholder={t('detail.createResponse.validityPlaceholder') || '30'}
                                                value={responseValidity}
                                                onChange={setResponseValidity}
                                                size="md"
                                            />
                                            <input
                                                type="number"
                                                min="1"
                                                className="hidden"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>

                                    <TextArea
                                        label={t('detail.createResponse.terms') || 'Termini e Condizioni'}
                                        placeholder={t('detail.createResponse.termsPlaceholder') || 'Preventivo valido per 30 giorni. Pagamento anticipato richiesto.'}
                                        value={responseTerms}
                                        onChange={setResponseTerms}
                                        rows={3}
                                    />

                                    <div className="flex items-center gap-3 pt-4 border-t border-secondary">
                                        <Button
                                            onClick={handleSaveDraft}
                                            color="secondary"
                                            size="md"
                                            iconLeading={Save01}
                                        >
                                            {t('detail.createResponse.saveDraft')}
                                        </Button>
                                        <Button
                                            onClick={handleSendResponse}
                                            color="primary"
                                            size="md"
                                            iconLeading={Send01}
                                            isDisabled={!responseSubject.trim() || !responseBody.trim() || isSendingResponse}
                                            isLoading={isSendingResponse}
                                        >
                                            {isSendingResponse
                                                ? (t('detail.createResponse.sending') || 'Invio in corso...')
                                                : t('detail.createResponse.send')}
                                        </Button>
                                    </div>
                                </div>

                                {/* Extracted Data Reference */}
                                {quote.extractedData && (
                                    <div className="lg:border-l lg:border-secondary lg:pl-6">
                                        <h3 className="text-md font-semibold text-primary mb-4">
                                            {t('detail.createResponse.extractedDataReference')}
                                        </h3>
                                        <div className="rounded-lg bg-secondary p-4 max-h-[600px] overflow-y-auto">
                                            <ExtractedDataPanel data={quote.extractedData} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                )}

                {/* Extracted Data Tab */}
                {activeTab === 'extracted' && quote.extractedData && (
                    <ExtractedDataPanel data={quote.extractedData} />
                )}
            </div>

            {/* Success Modal */}
            <ModalOverlay isOpen={showSuccessModal} onOpenChange={setShowSuccessModal}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                            <div className="flex items-center justify-center mb-4">
                                <div className="rounded-full bg-success-100 p-3">
                                    <CheckCircle className="size-6 text-success-600" />
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold text-primary text-center mb-2">
                                {t('detail.createResponse.successTitle') || 'Preventivo inviato con successo'}
                            </h2>
                            <p className="text-sm text-tertiary text-center mb-6">
                                {t('detail.createResponse.successMessage') || 'Il preventivo è stato inviato correttamente allo Shipper. Riceverà una notifica e potrà visualizzare la tua proposta nella sezione Quote.'}
                            </p>
                            <div className="flex justify-center">
                                <Button
                                    onClick={() => {
                                        console.log('[QuoteDetailPanel] Closing success modal');
                                        setShowSuccessModal(false);
                                    }}
                                    color="primary"
                                    size="md"
                                >
                                    {t('common:close') || 'Chiudi'}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>

            {/* Booking Share Success Modal */}
            <ModalOverlay isOpen={showBookingShareSuccessModal} onOpenChange={setShowBookingShareSuccessModal}>
                <Modal>
                    <Dialog>
                        <div className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md">
                            <div className="flex items-center justify-center mb-4">
                                <div className="rounded-full bg-success-100 p-3">
                                    <CheckCircle className="size-6 text-success-600" />
                                </div>
                            </div>
                            <h2 className="text-xl font-semibold text-primary text-center mb-2">
                                {t('detail.thread.bookingShareSuccessTitle') || 'Booking number condiviso con successo'}
                            </h2>
                            <p className="text-sm text-tertiary text-center mb-6">
                                {t('detail.thread.bookingShareSuccessMessage') || 'Il booking number è stato condiviso correttamente con lo Shipper. Riceverà una notifica e potrà visualizzare il booking number nella sezione Quote.'}
                            </p>
                            <div className="flex justify-center">
                                <Button
                                    onClick={() => {
                                        setShowBookingShareSuccessModal(false);
                                    }}
                                    color="primary"
                                    size="md"
                                >
                                    {t('common:close') || 'Chiudi'}
                                </Button>
                            </div>
                        </div>
                    </Dialog>
                </Modal>
            </ModalOverlay>
        </div>
    );
};

